export const OSCILLATION_WINDOW_MS = 1000;

export interface EmbedSize {
  height: number;
  width: number;
}

interface Measurement extends EmbedSize {
  at: number;
}

export interface EmbedHeightState {
  recent: Measurement[];
  reported: number | null;
  pinned: EmbedSize[] | null;
}

export const initialEmbedHeightState: EmbedHeightState = {
  recent: [],
  reported: null,
  pinned: null,
};

const isSameSize = (a: EmbedSize, b: EmbedSize): boolean =>
  a.height === b.height && a.width === b.width;

const findWidthCycle = (
  recent: Measurement[],
  measured: EmbedSize,
): EmbedSize[] | null => {
  const repeatIndex = recent.findIndex((size) => isSameSize(size, measured));
  if (repeatIndex === -1) return null;

  const cycle = recent.slice(repeatIndex);
  const widths = new Set(cycle.map(({ width }) => width));
  return widths.size > 1 ? cycle : null;
};

export const nextEmbedHeight = (
  state: EmbedHeightState,
  measured: EmbedSize,
  now: number,
  force = false,
): { state: EmbedHeightState; height: number | null } => {
  const { recent, reported, pinned } = state;
  const last = recent[recent.length - 1];

  if (
    pinned?.some((size) => isSameSize(size, measured)) ||
    (last && isSameSize(last, measured))
  ) {
    return { state, height: force ? reported : null };
  }

  const recentInWindow = pinned
    ? []
    : recent.filter(({ at }) => now - at <= OSCILLATION_WINDOW_MS);
  const cycle = findWidthCycle(recentInWindow, measured);
  const height = cycle
    ? Math.max(...cycle.map((size) => size.height))
    : measured.height;

  return {
    state: {
      recent: [...recentInWindow, { ...measured, at: now }],
      reported: height,
      pinned: cycle,
    },
    height: force || height !== reported ? height : null,
  };
};
